/**
 * MONSTER TOWN — WALKING. MONSTER CREW (src/monster/**).
 *
 * Shared by the kid's monster and every neighbour, so they all move with the
 * same weight: ease in, ease out, turn before you go, and slide along anything
 * you bump into rather than stopping dead.
 *
 * NO PATHFINDING ON PURPOSE. A* on a grid a 3-year-old is actively rearranging
 * costs CPU on an old iPad and produces walking that looks robotic. Instead the
 * walker steers straight at the target and slides on collision; if it truly
 * cannot get closer it simply arrives where it is. Nothing is ever failed
 * (LAW #2) and the kid always gets a reaction from their tap.
 */

import type { Grid } from '../core/grid'
import type { Vec3 } from '../core/types'

const TURN_RATE = 9 // rad/s
const ACCEL = 14 // units/s^2
const BRAKE = 18
/**
 * Slack on the arrival test. WITHOUT THIS THE GAME QUIETLY BREAKS: the step is
 * clamped to `dist - arriveDist`, so in floating point the walker converges on
 * the stop ring and `dist <= arriveDist` can stay false forever. The monster
 * stops moving, `play:arrived` never fires, and the thing the kid tapped never
 * reacts — LAW #1 broken by a rounding error. Found by driving a real tap.
 */
const ARRIVE_EPS = 0.06
/**
 * If we stop making progress at all, we are there as far as a kid is concerned.
 *
 * MEASURED, NOT INTENDED (venue fix, r3). This used to compare `move` — the
 * distance the walker WANTED to travel this step — which is a full stride
 * whenever the target is far away, so it could never trip. Meanwhile the slide
 * fallback below can "succeed" by moving the walker along an axis it is barely
 * travelling on: heading almost due north into a counter, `nx` is within a
 * rounding error of `this.x`, `isWorldWalkable(nx, this.z)` is trivially true,
 * `stuckFor` is reset to zero, and the walker sits there at full speed moving
 * 1e-15 of a unit per frame FOREVER.
 *
 * That is not theoretical: driving a real tap at the ice cream shop's cone
 * stack from the middle of the floor reproduced it every time. The monster set
 * off, froze at (-14, 27.579) with `walking: true` and `speed: 4.6`, and
 * `play:interacted` never fired — so a cone could never reach the tray, no
 * order could ever be matched, and the venue paid zero coins for as long as you
 * played it. The whole earn loop, dead, from one reset counter.
 *
 * Both detectors now measure the distance the walker ACTUALLY moved.
 */
const NO_PROGRESS_MIN = 0.004
const NO_PROGRESS_SECONDS = 0.4

export interface WalkerOptions {
  maxSpeed?: number
  /** How close counts as "there". */
  arriveDist?: number
}

export class Walker {
  x = 0
  z = 0
  /** Yaw in radians; 0 faces +Z (south), matching the art's forward. */
  facing = 0
  speed = 0
  maxSpeed: number
  arriveDist: number

  private tx = 0
  private tz = 0
  private active = false
  /** Set when the walker gave up because everything around it is blocked. */
  private stuckFor = 0
  /** Seconds spent moving less than a hair per step. */
  private crawlFor = 0

  constructor(opts: WalkerOptions = {}) {
    this.maxSpeed = opts.maxSpeed ?? 4.4
    this.arriveDist = opts.arriveDist ?? 0.32
  }

  get walking(): boolean {
    return this.active
  }

  get target(): Vec3 {
    return { x: this.tx, y: 0, z: this.tz }
  }

  /** Normalised 0..1 for the animator. */
  get gait(): number {
    return this.maxSpeed <= 0 ? 0 : Math.min(1, this.speed / this.maxSpeed)
  }

  setPosition(x: number, z: number): void {
    this.x = x
    this.z = z
    this.tx = x
    this.tz = z
    this.active = false
    this.speed = 0
    this.stuckFor = 0
    this.crawlFor = 0
  }

  walkTo(x: number, z: number, stopDist = this.arriveDist): void {
    this.tx = x
    this.tz = z
    this.arriveDist = stopDist
    this.active = true
    this.stuckFor = 0
    this.crawlFor = 0
  }

  stop(): void {
    this.active = false
    this.speed = 0
  }

  /** Face a world point without moving (used when an NPC greets the player). */
  faceTowards(x: number, z: number): void {
    const dx = x - this.x
    const dz = z - this.z
    if (Math.hypot(dx, dz) > 0.001) this.facing = Math.atan2(dx, dz)
  }

  /**
   * Advance one fixed step. Returns true on the frame the walker arrives.
   * `grid` may be null for scenes where nothing blocks movement.
   */
  step(dt: number, grid: Grid | null): boolean {
    const dx = this.tx - this.x
    const dz = this.tz - this.z
    const dist = Math.hypot(dx, dz)

    if (!this.active) {
      this.speed = Math.max(0, this.speed - BRAKE * dt)
      return false
    }

    if (dist <= this.arriveDist + ARRIVE_EPS) {
      this.active = false
      this.speed = Math.max(0, this.speed - BRAKE * dt)
      this.crawlFor = 0
      return true
    }

    // Turn toward the target first — a monster that pivots reads as alive.
    const want = Math.atan2(dx, dz)
    let turn = want - this.facing
    while (turn > Math.PI) turn -= Math.PI * 2
    while (turn < -Math.PI) turn += Math.PI * 2
    const maxTurn = TURN_RATE * dt
    this.facing += Math.max(-maxTurn, Math.min(maxTurn, turn))

    // Slow down for sharp turns and when nearly there — no skidding stops.
    const alignment = Math.max(0.25, Math.cos(Math.max(-Math.PI, Math.min(Math.PI, turn))))
    const approach = Math.min(1, (dist - this.arriveDist) / 1.2)
    const wanted = this.maxSpeed * alignment * Math.max(0.15, approach)
    this.speed += Math.max(-BRAKE * dt, Math.min(ACCEL * dt, wanted - this.speed))

    const move = Math.min(this.speed * dt, dist - this.arriveDist)
    const wasX = this.x
    const wasZ = this.z
    const ux = dx / dist
    const uz = dz / dist
    const nx = this.x + ux * move
    const nz = this.z + uz * move

    /**
     * THE ESCAPE HATCH — and the reason half the game looked dead.
     *
     * The collision test below asks "may I move INTO that cell", and it was the
     * only gate on movement. It has no answer for a monster that is already
     * standing ON a blocked cell: every candidate step, and both slide
     * fallbacks, are still inside that same cell, so all three fail, `stuckFor`
     * runs out and the walker reports that it has "arrived" without moving a
     * millimetre. It then does that for every tap, forever.
     *
     * A monster ends up on a blocked cell constantly and by design: a kid drops
     * a table on the square it is standing on (build mode encourages exactly
     * that), a demo lays a room out around it, or a scene spawns it on a tile
     * that furniture later claims. Driving a real tap at the telly across the
     * finished room reproduced it every time — the tap was accepted, the
     * interaction fired from where the monster stood, and to a kid the whole
     * room read as "nothing I touch does anything". LAW #1 broken by the floor.
     *
     * So: while the walker is standing somewhere it is not allowed to be, the
     * gate does not apply and it simply walks out. It becomes solid again the
     * moment it reaches a cell it is allowed on, which is at most a tile away,
     * and every target it is given has already been through `nearestWalkable`.
     */
    const trapped = grid !== null && !grid.isWorldWalkable(this.x, this.z)

    if (grid === null || trapped || grid.isWorldWalkable(nx, nz)) {
      this.x = nx
      this.z = nz
      this.stuckFor = 0
    } else if (grid.isWorldWalkable(nx, this.z)) {
      // Blocked: slide along whichever axis is still free.
      this.x = nx
      this.stuckFor = 0
    } else if (grid.isWorldWalkable(this.x, nz)) {
      this.z = nz
      this.stuckFor = 0
    } else {
      // Truly boxed in. Give up gracefully and count it as arriving.
      this.stuckFor += dt
      if (this.stuckFor > 0.35) {
        this.active = false
        this.speed = 0
        this.stuckFor = 0
        this.crawlFor = 0
        return true
      }
    }

    /**
     * THE ONLY HONEST PROGRESS TEST: how far did we ACTUALLY move.
     *
     * A slide that shifts the walker by a rounding error is not walking, and
     * that is exactly what the fallbacks above produce when the blocked
     * direction is the one carrying nearly all of the motion. Measuring the
     * real displacement makes both a hard wall and a glancing one resolve the
     * same way — the monster arrives, and whatever the kid tapped reacts.
     */
    const moved = Math.hypot(this.x - wasX, this.z - wasZ)
    this.crawlFor = moved < NO_PROGRESS_MIN ? this.crawlFor + dt : 0
    if (this.crawlFor > NO_PROGRESS_SECONDS) {
      this.active = false
      this.speed = 0
      this.crawlFor = 0
      this.stuckFor = 0
      return true
    }
    return false
  }
}

/**
 * Nearest walkable world point to (x,z), searched outward in rings. Used when a
 * kid taps a spot that is covered by a wall — the monster goes as close as it
 * can instead of refusing.
 */
export function nearestWalkable(grid: Grid, x: number, z: number, maxRings = 4): Vec3 {
  if (grid.isWorldWalkable(x, z)) return { x, y: 0, z }
  const c = grid.worldToCell(x, z, 0)
  for (let r = 1; r <= maxRings; r++) {
    /**
     * NEAREST, NOT FIRST-SCANNED.
     *
     * This used to return the first walkable cell the loop tripped over, and
     * the loop walks dz from -r and dx from -r — so of the eight cells in ring
     * one it always preferred the NORTH-WEST one. "Nearest walkable" was
     * nearest only by accident, and a standoff point blocked by a rug or a
     * shelf sent the monster up to a tile and a half the wrong way, routinely
     * to the far side of the thing it was walking to.
     *
     * Scoring the whole ring and taking the closest costs eight distance tests
     * on a tap and makes the name honest.
     */
    let best: Vec3 | null = null
    let bestD = Infinity
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue
        if (!grid.isWalkable(c.gx + dx, c.gz + dz)) continue
        const p = grid.cellToWorld({ gx: c.gx + dx, gy: 0, gz: c.gz + dz })
        const d = (p.x - x) * (p.x - x) + (p.z - z) * (p.z - z)
        if (d < bestD) {
          bestD = d
          best = p
        }
      }
    }
    if (best !== null) return best
  }
  return { x, y: 0, z }
}

/** A world point just outside a footprint, on the side the walker is on. */
export function standoffPoint(from: Vec3, at: Vec3, distance: number): Vec3 {
  const dx = from.x - at.x
  const dz = from.z - at.z
  const d = Math.hypot(dx, dz)
  if (d < 0.001) return { x: at.x, y: 0, z: at.z + distance }
  return { x: at.x + (dx / d) * distance, y: 0, z: at.z + (dz / d) * distance }
}
